import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, UserPlus, Check, Ban, RotateCcw, ChevronDown, Users, ClipboardList, KeyRound, Copy } from "lucide-react";
import { TopBar } from "../components/TopBar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RoleBadge, StatusBadge } from "../components/Badges";
import AdminCasesTab from "../components/admin/AdminCasesTab";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { api } from "../lib/api";
import { toast } from "sonner";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  } catch {
    return "—";
  }
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", role: "all", status: "all" });
  const [createOpen, setCreateOpen] = useState(false);
  // Phase 13 — admin-driven password reset
  const [resetConfirmFor, setResetConfirmFor] = useState(null); // user obj
  const [resetResult, setResetResult] = useState(null); // {user, password} | null
  const [resetBusy, setResetBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  // Phase 17 — storage cleanup modal state
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupReport, setCleanupReport] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);

  const openStorageCleanup = async () => {
    setCleanupBusy(true);
    setCleanupReport(null);
    setCleanupOpen(true);
    try {
      const { data } = await api.post(
        "/admin/storage/cleanup-orphans?dry_run=true&cleanup_blobs=true"
      );
      setCleanupReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err_generic"));
      setCleanupOpen(false);
    } finally {
      setCleanupBusy(false);
    }
  };

  const applyStorageCleanup = async () => {
    setCleanupBusy(true);
    try {
      const { data } = await api.post(
        "/admin/storage/cleanup-orphans?dry_run=false&cleanup_blobs=true"
      );
      setCleanupReport(data);
      toast.success(
        `Cleaned ${data.orphans_cleaned} orphan files + ${data.blob_orphans_cleaned} orphan blobs.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("err_generic"));
    } finally {
      setCleanupBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.role !== "all") params.role = filters.role;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.q.trim()) params.q = filters.q.trim();
      const { data } = await api.get("/admin/users", { params });
      setUsers(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = { admin: 0, agent: 0, reviewer: 0, pending: 0 };
    for (const u of users) {
      c[u.role] = (c[u.role] || 0) + 1;
      if (u.status === "pending_approval") c.pending += 1;
    }
    return c;
  }, [users]);

  const handleApprove = async (id) => {
    try {
      await api.post(`/admin/users/${id}/approve`);
      toast.success(t("toast_approve_success"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    }
  };
  const handleDisable = async (id) => {
    try {
      await api.post(`/admin/users/${id}/disable`);
      toast.success(t("toast_disable_success"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    }
  };
  const handleEnable = async (id) => {
    try {
      await api.post(`/admin/users/${id}/enable`);
      toast.success(t("toast_enable_success"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    }
  };
  const handleRoleChange = async (id, role) => {
    try {
      await api.patch(`/admin/users/${id}/role`, { role });
      toast.success(t("toast_role_success"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    }
  };

  // Phase 13 — generate a one-time temporary password for the selected user
  const handleResetPassword = async (u) => {
    setResetBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setResetConfirmFor(null);
      setResetResult({ user: u, password: data.temporary_password });
      setCopied(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    } finally {
      setResetBusy(false);
    }
  };

  const copyTempPassword = async () => {
    if (!resetResult) return;
    try {
      await navigator.clipboard.writeText(resetResult.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(t("err_generic"));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold mb-1">
              Admin
            </p>
            <h2 className="font-display text-3xl tracking-tight font-medium text-[#0A2540]">
              {tab === "users" ? t("admin_heading") : t("admin_cases_heading")}
            </h2>
            <p className="text-slate-500 text-sm mt-1">{t("admin_subheading")}</p>
          </div>
          {tab === "users" ? (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={openStorageCleanup}
                disabled={cleanupBusy}
                className="border-amber-300 text-amber-700 hover:bg-amber-50 h-10"
                data-testid="open-storage-cleanup-button"
              >
                <KeyRound className="h-4 w-4 mr-2 rotate-90" />
                {t("storage_cleanup")}
              </Button>
              <Button
                onClick={() => setCreateOpen(true)}
                className="bg-[#0A2540] hover:bg-[#051626] text-white h-10"
                data-testid="open-create-reviewer-button"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {t("admin_create_reviewer")}
              </Button>
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-2 border-b border-slate-200" data-testid="admin-tabs">
          {[
            { v: "users", l: t("admin_tab_users"), icon: Users },
            { v: "cases", l: t("admin_tab_cases"), icon: ClipboardList },
          ].map((tabDef) => {
            const Icon = tabDef.icon;
            const active = tab === tabDef.v;
            return (
              <button
                key={tabDef.v}
                type="button"
                onClick={() => setTab(tabDef.v)}
                className={`px-4 py-2.5 text-sm font-medium transition-colors focus-ring inline-flex items-center gap-2 border-b-2 -mb-px ${
                  active
                    ? "border-[#0A2540] text-[#0A2540]"
                    : "border-transparent text-slate-500 hover:text-[#0A2540]"
                }`}
                data-testid={`admin-tab-${tabDef.v}`}
              >
                <Icon className="h-4 w-4" />
                {tabDef.l}
              </button>
            );
          })}
        </div>

        {tab === "cases" ? <AdminCasesTab /> : (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label={t("role_admin")} value={counts.admin} accent="bg-slate-100 text-slate-700" />
          <StatCard label={t("role_agent")} value={counts.agent} accent="bg-blue-50 text-blue-700" />
          <StatCard label={t("role_reviewer")} value={counts.reviewer} accent="bg-indigo-50 text-indigo-700" />
          <StatCard label={t("status_pending_approval")} value={counts.pending} accent="bg-amber-50 text-amber-700" />
        </div>

        <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="px-4 sm:px-6 py-4 border-b border-slate-200 grid gap-3 md:grid-cols-[1fr_180px_180px]">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                placeholder={t("admin_filter_search")}
                className="pl-9"
                data-testid="admin-filter-search"
              />
            </div>
            <Select
              value={filters.role}
              onValueChange={(v) => setFilters((f) => ({ ...f, role: v }))}
            >
              <SelectTrigger data-testid="admin-filter-role">
                <SelectValue placeholder={t("admin_filter_role")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="admin-filter-role-all">{t("admin_filter_all")} · {t("admin_filter_role")}</SelectItem>
                <SelectItem value="admin" data-testid="admin-filter-role-admin">{t("role_admin")}</SelectItem>
                <SelectItem value="agent" data-testid="admin-filter-role-agent">{t("role_agent")}</SelectItem>
                <SelectItem value="reviewer" data-testid="admin-filter-role-reviewer">{t("role_reviewer")}</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.status}
              onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            >
              <SelectTrigger data-testid="admin-filter-status">
                <SelectValue placeholder={t("admin_filter_status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="admin-filter-status-all">{t("admin_filter_all")} · {t("admin_filter_status")}</SelectItem>
                <SelectItem value="active" data-testid="admin-filter-status-active">{t("status_active")}</SelectItem>
                <SelectItem value="pending_approval" data-testid="admin-filter-status-pending">{t("status_pending_approval")}</SelectItem>
                <SelectItem value="disabled" data-testid="admin-filter-status-disabled">{t("status_disabled")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="admin-users-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-6 py-3 text-left">{t("admin_col_user")}</th>
                  <th className="px-6 py-3 text-left">{t("admin_col_role")}</th>
                  <th className="px-6 py-3 text-left">{t("admin_col_status")}</th>
                  <th className="px-6 py-3 text-left">{t("admin_col_joined")}</th>
                  <th className="px-6 py-3 text-right">{t("admin_col_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-slate-400">
                      <div className="inline-block h-6 w-6 border-2 border-slate-300 border-t-[#0A2540] rounded-full animate-spin" />
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-slate-400" data-testid="admin-users-empty">
                      {t("admin_empty")}
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr
                      key={u.id}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      data-testid={`user-row-${u.email}`}
                    >
                      <td className="px-6 py-4 align-top">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-slate-100 text-[#0A2540] flex items-center justify-center font-medium text-sm">
                            {(u.name || u.email).slice(0, 1).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-[#0A2540]">{u.name}</div>
                            <div className="text-xs text-slate-500">{u.email}</div>
                            {u.agent_number ? (
                              <div className="text-xs text-slate-400 mt-0.5">#{u.agent_number}</div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 align-top">
                        <RoleBadge role={u.role} />
                      </td>
                      <td className="px-6 py-4 align-top">
                        <StatusBadge status={u.status} />
                      </td>
                      <td className="px-6 py-4 align-top text-sm text-slate-500">
                        {formatDate(u.created_at)}
                      </td>
                      <td className="px-6 py-4 align-top">
                        <div className="flex items-center justify-end gap-1">
                          {u.status === "pending_approval" ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleApprove(u.id)}
                              data-testid={`approve-user-${u.email}`}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              {t("admin_action_approve")}
                            </Button>
                          ) : null}
                          {u.status === "disabled" ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleEnable(u.id)}
                              data-testid={`enable-user-${u.email}`}
                            >
                              <RotateCcw className="h-4 w-4 mr-1" />
                              {t("admin_action_enable")}
                            </Button>
                          ) : null}
                          {u.status === "active" && u.id !== user?.id ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-rose-700 hover:bg-rose-50"
                              onClick={() => handleDisable(u.id)}
                              data-testid={`disable-user-${u.email}`}
                            >
                              <Ban className="h-4 w-4 mr-1" />
                              {t("admin_action_disable")}
                            </Button>
                          ) : null}

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-slate-600 hover:bg-slate-100"
                                data-testid={`change-role-trigger-${u.email}`}
                              >
                                {t("admin_action_change_role")}
                                <ChevronDown className="h-4 w-4 ml-1" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>{t("admin_action_change_role")}</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                disabled={u.role === "admin"}
                                onClick={() => handleRoleChange(u.id, "admin")}
                                data-testid={`change-role-admin-${u.email}`}
                              >
                                {t("role_admin")}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={u.role === "agent"}
                                onClick={() => handleRoleChange(u.id, "agent")}
                                data-testid={`change-role-agent-${u.email}`}
                              >
                                {t("role_agent")}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={u.role === "reviewer"}
                                onClick={() => handleRoleChange(u.id, "reviewer")}
                                data-testid={`change-role-reviewer-${u.email}`}
                              >
                                {t("role_reviewer")}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>

                          {/* Phase 13 — Reset Password */}
                          {u.id !== user?.id ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-amber-700 hover:bg-amber-50"
                              onClick={() => setResetConfirmFor(u)}
                              data-testid={`reset-password-${u.email}`}
                              title={t("reset_password")}
                            >
                              <KeyRound className="h-4 w-4 mr-1" />
                              {t("reset_password")}
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>)}
      </main>

      <CreateReviewerDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={load}
      />

      {/* Phase 13 — confirm dialog before resetting another user's password */}
      <Dialog open={!!resetConfirmFor} onOpenChange={(o) => !o && setResetConfirmFor(null)}>
        <DialogContent data-testid="reset-password-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{t("reset_password")}</DialogTitle>
            <DialogDescription>
              {resetConfirmFor
                ? t("reset_password_confirm").replace("{name}", resetConfirmFor.name)
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setResetConfirmFor(null)}
              disabled={resetBusy}
              data-testid="reset-password-cancel"
            >
              {t("modal_cancel")}
            </Button>
            <Button
              type="button"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => resetConfirmFor && handleResetPassword(resetConfirmFor)}
              disabled={resetBusy}
              data-testid="reset-password-confirm"
            >
              <KeyRound className="h-4 w-4 mr-1" />
              {t("reset_password")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 13 — one-time temp password display */}
      <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
        <DialogContent data-testid="temp-password-modal">
          <DialogHeader>
            <DialogTitle>{t("temp_password_modal_title")}</DialogTitle>
            <DialogDescription>
              {resetResult
                ? t("temp_password_modal_helper").replace("{name}", resetResult.user.name)
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="my-2 flex items-stretch gap-2">
            <div
              className="flex-1 font-mono text-lg tracking-wider bg-slate-50 border border-slate-200 rounded-md px-4 py-3 select-all"
              data-testid="temp-password-value"
            >
              {resetResult?.password || ""}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={copyTempPassword}
              className="border-slate-300"
              data-testid="temp-password-copy"
            >
              <Copy className="h-4 w-4 mr-1" />
              {copied ? t("copied") : t("copy")}
            </Button>
          </div>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
            {t("temp_password_shown_once")}
          </p>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => setResetResult(null)}
              className="bg-[#0A2540] hover:bg-[#051626] text-white"
              data-testid="temp-password-ok"
            >
              {t("temp_password_ok_button")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Phase 17 — Storage cleanup modal */}
      <Dialog open={cleanupOpen} onOpenChange={(o) => { if (!o) { setCleanupOpen(false); setCleanupReport(null); } }}>
        <DialogContent className="sm:max-w-2xl" data-testid="storage-cleanup-dialog">
          <DialogHeader>
            <DialogTitle>{t("storage_cleanup")}</DialogTitle>
            <DialogDescription>{t("storage_cleanup_helper")}</DialogDescription>
          </DialogHeader>
          {cleanupBusy && !cleanupReport ? (
            <p className="text-sm text-slate-500">Scanning…</p>
          ) : cleanupReport ? (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-slate-50 border border-slate-200 rounded p-2">
                  <p className="text-xs text-slate-500">Scanned</p>
                  <p className="font-medium">{cleanupReport.scanned}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded p-2">
                  <p className="text-xs text-slate-500">Orphans found</p>
                  <p className="font-medium text-amber-700">{cleanupReport.orphans_found}</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                  <p className="text-xs text-slate-500">Cleaned</p>
                  <p className="font-medium text-emerald-700">{cleanupReport.orphans_cleaned}</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded p-2">
                  <p className="text-xs text-slate-500">Cases affected</p>
                  <p className="font-medium">{cleanupReport.cases_affected}</p>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Orphan blobs: <strong>{cleanupReport.blob_orphans_found}</strong> found,{" "}
                <strong>{cleanupReport.blob_orphans_cleaned}</strong> cleaned.
              </p>
              {cleanupReport.per_case_summary?.length ? (
                <div className="max-h-56 overflow-auto border border-slate-200 rounded p-2 space-y-1">
                  {cleanupReport.per_case_summary.map((c, i) => (
                    <div key={i} className="text-xs" data-testid={`cleanup-case-row-${c.case_id}`}>
                      <strong>Case {c.case_number}</strong> — {c.applicant_name}
                      {c.owner_agent_email ? ` · ${c.owner_agent_email}` : ""}
                      <span className="text-slate-500"> · slots: {c.slots_cleared?.join(", ") || "—"}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-emerald-700">No orphan files — storage is clean. ✅</p>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCleanupOpen(false); setCleanupReport(null); }} disabled={cleanupBusy}>
              {t("modal_cancel")}
            </Button>
            {cleanupReport && cleanupReport.dry_run && cleanupReport.orphans_found > 0 ? (
              <Button
                onClick={applyStorageCleanup}
                disabled={cleanupBusy}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="apply-storage-cleanup-button"
              >
                {t("storage_cleanup_apply")}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ label, value, accent }) => (
  <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 flex items-center justify-between">
    <div>
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className="font-display text-3xl tracking-tight font-medium text-[#0A2540] mt-1">{value}</p>
    </div>
    <span className={`text-xs px-2 py-0.5 rounded-full ${accent}`}>·</span>
  </div>
);

const CreateReviewerDialog = ({ open, onOpenChange, onCreated }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setForm({ name: "", email: "", password: "" });
      setError("");
    }
  }, [open]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/admin/reviewers", {
        ...form,
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
      });
      toast.success(t("toast_reviewer_created"));
      onOpenChange(false);
      onCreated && onCreated();
    } catch (err) {
      setError(err?.response?.data?.detail || t("err_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="create-reviewer-dialog">
        <DialogHeader>
          <DialogTitle>{t("modal_create_reviewer_title")}</DialogTitle>
          <DialogDescription>{t("modal_create_reviewer_desc")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cr-name">{t("modal_name")}</Label>
            <Input
              id="cr-name"
              required
              value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              data-testid="create-reviewer-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cr-email">{t("modal_email")}</Label>
            <Input
              id="cr-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
              data-testid="create-reviewer-email"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cr-pass">{t("modal_password")}</Label>
            <Input
              id="cr-pass"
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))}
              data-testid="create-reviewer-password"
            />
          </div>
          {error ? (
            <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2" data-testid="create-reviewer-error">
              {error}
            </div>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="create-reviewer-cancel"
            >
              {t("modal_cancel")}
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#0A2540] hover:bg-[#051626] text-white"
              data-testid="create-reviewer-submit"
            >
              {submitting ? "..." : t("modal_create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDashboard;
